import { areasOf, clamp, MAX_PRINT_AREAS, type Backdrop, type InkLook, type MockupJob, type MockupPrint, type MockupTemplate, type Placement, type Rgb } from '@eip/engine';
import type { MockupFormat, MockupPrepared, RunResult } from '../worker/protocol';
import { printWidthFor } from './sep';
import type { DocEntry, Settings } from './store';

/**
 * Where one template stands in the worker. Preparation (mask + shading maps) is
 * per TEMPLATE, not per document — several documents share one prepared blank —
 * so this is keyed by template id in the store rather than hung off a document.
 */
export type TemplateStatus = { state: 'idle' | 'preparing' | 'ready' | 'error'; prepared?: MockupPrepared; error?: string };

/**
 * Where one backdrop scene stands in the worker: its pixels are decoded on demand — a scene the
 * shop imported three sessions ago is a Blob in IndexedDB and nothing more until a job names it —
 * so a render that needs one has to wait exactly as it waits for a template's maps.
 *
 * Keyed by scene id in the store, not per document, for the same reason `TemplateStatus` is: one
 * decoded scene serves every document and every blank it is rendered behind. There is no
 * `prepared` payload to carry — the worker holds the image, the size is on the `SceneSource`.
 */
export type SceneStatus = { state: 'idle' | 'loading' | 'ready' | 'error'; error?: string };

/**
 * Which renderer the interactive mockup preview is actually using right now:
 * the GPU (`'gl'`, the WebGL2 path — a full simulation every animation frame),
 * the worker's CPU compositor (`'cpu'`, the fallback and the initial value), or
 * the CPU because the GL context was LOST (`'lost'`, which the banner says out
 * loud because the page was faster a moment ago and the user will notice).
 *
 * It lives in the store rather than in `mockup/gl-preview.ts` because three
 * unrelated places read it — the viewer, the inspector's Renderer row and the
 * App banner — and only the hook that drives the renderer knows when it moves.
 */
export type GlPreviewState = 'gl' | 'cpu' | 'lost';

/**
 * Per-document mockup state: the job being edited, the colourway the panel and
 * the exports work from, and the generation bookkeeping that keeps a stale
 * render from clobbering a newer one (the same discipline as `DocSep`).
 */
export interface DocMockup {
  job: MockupJob;
  /** The garment colours selected for this document, as hexes. The first is the
   *  one on screen (it drives `job.garment`); all of them drive the colourway
   *  sheet and the ZIP. */
  colors: string[];
  /** Another open document rendered into the style's back blank, for a front+back pair. */
  backDocId: string | null;
  /**
   * The job that back document is rendered WITH: its own placement, look and
   * realism on the style's BACK blank, in the colour this document's front tile
   * wears. Derived by the session (`backJobFor`) whenever this job, the bound
   * document's job or the registry moves; absent when nothing is bound or the
   * style has no back blank.
   *
   * It lives on the FRONT document because it belongs to the pair rather than
   * to either half: the back document's own `job` is what its own panel and
   * preview show (on a front blank, the only kind the panel offers), and two
   * different pairs could bind the same back design in two different colours.
   */
  backJob?: MockupJob;
  rendering: boolean;
  /** The generation of the most recently REQUESTED render. */
  renderGeneration?: number;
  /** The generation of the most recently APPLIED render — the one the cached bitmap belongs to. */
  renderedGeneration?: number;
  /** The `run.generation` the applied render composited, so the UI can tell a
   *  stale preview (the DTF output moved since) from a current one. */
  runGeneration?: number;
  /** True when the art did not fit the print area and was cropped by it. */
  clipped: boolean;
  /** The areas the last render could NOT print, from the compositor: an extra print naming an
   *  area this blank does not have, or a document that is closed (or has no DTF output yet).
   *  De-duplicated, and empty until a render says otherwise — the panel names them. */
  skipped: string[];
  error?: string;
  ms?: number;
  /** The back preview's own copy of the four fields above. A pair is two renders
   *  — this document's art on its front blank, and the bound document's art on
   *  the style's back blank — and the viewer's Front/Back toggle switches
   *  between them, so neither may overwrite the other's bookkeeping. The bitmap
   *  is cached under `mockupPreviewKey(id, 'back')`. */
  backRendering?: boolean;
  backRenderGeneration?: number;
  backRenderedGeneration?: number;
  backRunGeneration?: number;
  backClipped?: boolean;
  /** `skipped` for the back slot. A pair's two renders are two jobs with two sets of extra
   *  prints, and neither may overwrite the other's verdict. */
  backSkipped?: string[];
  backError?: string;
}

/** The shop's last-used mockup settings, seeded into every newly-touched document
 *  and persisted across sessions (never the per-document job itself). */
export interface MockupDefaults {
  look: InkLook;
  placement: Placement;
  sheet: { tileW: number; label: boolean };
  format: MockupFormat;
  /** `null` = use the template's own value (the usual case); a number overrides it. */
  wrinkle: number | null;
  texture: number | null;
  /** The template last chosen, re-used when seeding a document as long as it is
   *  still in the registry. Optional: absent in a payload written before it, and
   *  absent until the shop picks a template by hand. */
  lastTemplateId?: string;
}

export function defaultMockupDefaults(): MockupDefaults {
  return { look: 'screen', placement: { widthIn: 10, xIn: 'center', yIn: 1.5 }, sheet: { tileW: 800, label: true }, format: 'png', wrinkle: null, texture: null };
}

/** The placement ranges the panel's controls offer; persisted values are held to
 *  the same bounds, so nothing outside what could have been typed survives a reload. */
export const WIDTH_IN_MIN = 0.5, WIDTH_IN_MAX = 40;
export const OFFSET_IN_MIN = -10, OFFSET_IN_MAX = 40;
const TILE_MIN = 200, TILE_MAX = 2000;

/** Degrees clockwise about the art rect's centre. The compositor takes any finite angle; this is
 *  the range the panel's control offers, and what a stored or pasted job is held to. */
export const ROTATION_MIN = -180, ROTATION_MAX = 180;
/** Eight areas on a blank (`MAX_PRINT_AREAS`), one of which is the job's primary print. */
export const MAX_EXTRA_PRINTS = MAX_PRINT_AREAS - 1;

const isFinite_ = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const clampOr = (v: unknown, lo: number, hi: number, def: number): number => (isFinite_(v) ? clamp(v, lo, hi) : def);

/** One placement held to the ranges the panel's controls offer, field by field — the same rule
 *  everywhere, so an extra print cannot be given a position the primary could not have. */
function sanitizePlacement(raw: unknown, def: Placement): Placement {
  const p = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  return {
    widthIn: clampOr(p.widthIn, WIDTH_IN_MIN, WIDTH_IN_MAX, def.widthIn),
    xIn: p.xIn === 'center' ? 'center' : isFinite_(p.xIn) ? clamp(p.xIn, OFFSET_IN_MIN, OFFSET_IN_MAX) : def.xIn,
    yIn: p.yIn === 'center' ? 'center' : isFinite_(p.yIn) ? clamp(p.yIn, OFFSET_IN_MIN, OFFSET_IN_MAX) : def.yIn,
  };
}

/** A `Rgb` as the app is willing to hold one: exactly three finite numbers. Anything else — a
 *  hand-edited payload, a pasted job, a colour picker that wrote four components — is not a
 *  colour, and a backdrop built from it would render as black rather than as an error. */
const rgbOrNull = (v: unknown): Rgb | null =>
  (Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n)) ? [v[0], v[1], v[2]] : null);

/**
 * A job's backdrop as the app is willing to hold it, or `null` for "the photo's own".
 *
 * `scenes` is the scene list to check a `scene` backdrop against, and passing it is what makes a
 * backdrop pointing at a DELETED scene clear itself: the store hands over the list it holds, so a
 * job read back from anywhere (the panel, a colourway switch, a scene being removed) can only name
 * a scene that exists. Called WITHOUT a list — the list is not always known — the id stands, and
 * the runner's `ensureScene` is what reports a scene that has gone.
 *
 * Every accepted backdrop is rebuilt field by field: nothing the caller hung off the object rides
 * into the job, and the key order is fixed, which is what lets `mockupChangeKind` compare two of
 * them cheaply.
 */
function sanitizeBackdrop(raw: unknown, scenes?: ReadonlyArray<{ id: string }>): Backdrop | null {
  if (!raw || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  if (b.kind === 'color') { const rgb = rgbOrNull(b.rgb); return rgb ? { kind: 'color', rgb } : null; }
  if (b.kind === 'gradient') {
    const top = rgbOrNull(b.top), bottom = rgbOrNull(b.bottom);
    return top && bottom ? { kind: 'gradient', top, bottom } : null;
  }
  if (b.kind === 'scene') {
    const sceneId = typeof b.sceneId === 'string' ? b.sceneId : '';
    if (!sceneId) return null;
    // A scene the shop has deleted (or one from a payload written on another machine): the backdrop
    // goes back to the photo's own rather than leaving the job naming pixels nothing can produce.
    if (scenes && !scenes.some((s) => s.id === sceneId)) return null;
    return { kind: 'scene', sceneId };
  }
  return null;
}

/**
 * A job as the app is willing to hold it: the rotation clamped to the panel's range, the backdrop
 * validated (and held to the scenes that exist), and the extra prints held to areas this blank
 * actually has.
 *
 * `template` is the blank the job names, and the panel passes it so a print can only be made in
 * an area that blank HAS (and never in the PRIMARY one, which is `job.placement` and not an extra
 * at all). Every other caller — the store's own setter above all — passes none, and then the ids
 * stand: a job moved onto a blank with no sleeve keeps its sleeve print, the render reports the
 * area as skipped and the ticket says so. Deleting it instead would cost the shop the design they
 * chose the moment they looked at another colourway.
 *
 * One print per AREA, the first winning, whichever way round: the panel writes one row per area,
 * and a second print in the same one would composite it twice for no visible difference.
 *
 * `enabled` defaults to TRUE, not false: the compositor draws only enabled prints, so a job from
 * an older build — or a caller that forgot the flag — must not quietly print nothing.
 */
export function sanitizeJob(job: MockupJob, template?: MockupTemplate, scenes?: ReadonlyArray<{ id: string }>): MockupJob {
  const def = defaultMockupDefaults().placement;
  const areas = template ? areasOf(template) : undefined;
  // The primary area is `printAreas[0]`; an extra print there would fight `job.placement` for
  // the same rectangle, so the panel does not offer it and one arriving through it is dropped.
  const allowed = areas ? new Set(areas.slice(1).map((a) => a.id).filter((id): id is string => !!id)) : undefined;
  const raw = Array.isArray(job.extraPrints) ? job.extraPrints : [];
  const extraPrints: MockupPrint[] = [];
  const taken = new Set<string>();
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const areaId = typeof p.areaId === 'string' ? p.areaId : '';
    if (!areaId || taken.has(areaId)) continue;
    if (allowed && !allowed.has(areaId)) continue;
    taken.add(areaId);
    extraPrints.push({
      areaId,
      // Kept as typed: the document may be closed, and that is the render's verdict to report
      // (as `skipped`) rather than an edit to make behind the shop's back.
      docId: typeof p.docId === 'string' && p.docId ? p.docId : null,
      placement: sanitizePlacement(p.placement, def),
      rotationDeg: clampOr(p.rotationDeg, ROTATION_MIN, ROTATION_MAX, 0),
      enabled: p.enabled !== false,
    });
    if (extraPrints.length === MAX_EXTRA_PRINTS) break;
  }
  return {
    ...job,
    placement: sanitizePlacement(job.placement, def),
    rotationDeg: clampOr(job.rotationDeg, ROTATION_MIN, ROTATION_MAX, 0),
    extraPrints,
    // Always present, and `null` rather than absent: every reader of a job compares backdrops
    // (`mockupChangeKind`, the blank cache key), and "the field is missing" and "there is no
    // backdrop" have to be one thing for those comparisons to be stable.
    backdrop: sanitizeBackdrop(job.backdrop, scenes),
  };
}

/**
 * Per-field validation of persisted mockup defaults, the same rule the settings
 * and separation defaults follow: every field falls back on its own, so one bad
 * entry cannot take the rest with it. `wrinkle`/`texture` accept `null` as a real
 * value ("use the template's") distinct from "absent".
 */
export function sanitizeMockupDefaults(raw: unknown): MockupDefaults {
  const d = defaultMockupDefaults();
  const r = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const sheet = r.sheet && typeof r.sheet === 'object' ? r.sheet as Record<string, unknown> : {};
  const out: MockupDefaults = {
    look: r.look === 'dtf' || r.look === 'screen' ? r.look : d.look,
    placement: sanitizePlacement(r.placement, d.placement),
    sheet: { tileW: clampOr(sheet.tileW, TILE_MIN, TILE_MAX, d.sheet.tileW), label: typeof sheet.label === 'boolean' ? sheet.label : d.sheet.label },
    format: r.format === 'jpeg' || r.format === 'png' ? r.format : d.format,
    wrinkle: r.wrinkle === null ? null : isFinite_(r.wrinkle) ? clamp(r.wrinkle, 0, 0.03) : d.wrinkle,
    texture: r.texture === null ? null : isFinite_(r.texture) ? clamp(r.texture, 0, 1) : d.texture,
  };
  if (typeof r.lastTemplateId === 'string' && r.lastTemplateId) out.lastTemplateId = r.lastTemplateId;
  return out;
}

/**
 * A document's own mockup if it has one, else a fresh job on `templateId` from
 * the shop defaults, with the art placed at the width this document actually
 * prints at — `printWidthFor`, the same rule the separations use (the settings
 * bar's target width, else the native width of the DTF OUTPUT, which trim has
 * usually narrowed). With no output yet there is nothing to measure, so the
 * shop's remembered width stands rather than `printWidthFor`'s half-inch floor.
 */
export function mockupFor(prev: DocMockup | undefined, defaults: MockupDefaults, templateId: string, settings: Settings, run: RunResult | undefined): DocMockup {
  if (prev) return prev;
  const widthIn = clamp(run ? printWidthFor(settings, run) : settings.targetWidthIn ?? defaults.placement.widthIn, WIDTH_IN_MIN, WIDTH_IN_MAX);
  return {
    job: {
      templateId,
      look: defaults.look,
      placement: { widthIn, xIn: defaults.placement.xIn, yIn: defaults.placement.yIn },
      // 3C-1 defaults: an unturned single print. The panel owns both from here.
      rotationDeg: 0,
      extraPrints: [],
      garment: null,
      wrinkle: defaults.wrinkle,
      texture: defaults.texture,
      // 3C-3: the blank's own studio backdrop. The panel owns it from here.
      backdrop: null,
    },
    colors: [],
    backDocId: null,
    rendering: false,
    clipped: false,
    skipped: [],
  };
}

/**
 * What a job change requires. Every field of a `MockupJob` feeds the compositor,
 * so there is no cheap middle path as there is for separations (`films`): either
 * nothing moved or the mockup is re-rendered. Compared field by field rather than
 * by identity so that a panel rebuilding an equal job (a slider dragged back to
 * where it started) does not cost a render.
 */
export function mockupChangeKind(prev: MockupJob, next: MockupJob): 'none' | 'render' {
  // Extra prints are keyed field by field and IN ORDER: they composite over one another in the
  // order they are listed, so two of them swapped is a different picture. A normalised array
  // rather than the objects themselves, so a job rebuilt with the fields in another order (or
  // carrying one the compositor ignores) does not read as a change.
  const prints = (j: MockupJob) => (j.extraPrints ?? []).map((p) => [p.areaId, p.docId, p.placement.widthIn, p.placement.xIn, p.placement.yIn, p.rotationDeg, p.enabled]);
  // Field by field rather than the object itself, so a backdrop rebuilt with its keys in another
  // order does not read as a change — and a missing field is the same as `null`, which is what a
  // job written before 3C-3 carries.
  const backdrop = (j: MockupJob) => {
    const b = j.backdrop;
    if (!b) return null;
    return b.kind === 'color' ? ['color', b.rgb] : b.kind === 'gradient' ? ['gradient', b.top, b.bottom] : ['scene', b.sceneId];
  };
  const key = (j: MockupJob) => JSON.stringify([j.templateId, j.look, j.placement.widthIn, j.placement.xIn, j.placement.yIn, j.garment, j.wrinkle, j.texture, j.rotationDeg ?? 0, prints(j), backdrop(j)]);
  return key(prev) === key(next) ? 'none' : 'render';
}

/** The same comparison over jobs that may be absent, for the derived `backJob`:
 *  two missing jobs are the same job, and a job that appeared or disappeared is
 *  a change (a pair bound or unbound). */
export function sameMockupJob(a: MockupJob | undefined, b: MockupJob | undefined): boolean {
  if (!a || !b) return a === b;
  return mockupChangeKind(a, b) === 'none';
}

/**
 * Whether this document can show a current mockup, and if not, what is missing —
 * the panel's status line and the inspector's hint both read this.
 *
 * `stale` means a render LANDED but from an older DTF output; a document that has
 * simply not rendered yet is `ok` (the render is either running or one trigger
 * away, and there is nothing for the user to fix).
 */
export function mockupReadiness(doc: DocEntry, templateStatus: TemplateStatus | undefined): 'ok' | 'no-run' | 'no-template' | 'preparing' | 'stale' {
  if (!doc.run) return 'no-run';
  if (!doc.mockup) return 'no-template';
  if (!templateStatus || templateStatus.state === 'idle' || templateStatus.state === 'error') return 'no-template';
  if (templateStatus.state === 'preparing') return 'preparing';
  if (doc.mockup.renderedGeneration !== undefined && doc.mockup.runGeneration !== doc.run.generation) return 'stale';
  return 'ok';
}

/** What the pinned footer's mockup status line is saying. */
export type MockupStatusKind = 'none' | 'no-run' | 'no-template' | 'preparing' | 'rendering' | 'error' | 'stale' | 'rendered';

/**
 * The footer's mockup status as one value, so everything that reacts to it
 * reacts to the same one: `PanelFooter` paints it (and paints `error` in the
 * fail colour), and the inspector's summary dot goes red on `error` — a green
 * dot above a red status line would be the panel disagreeing with itself.
 *
 * Unlike the panel body this is mounted in mockup mode whatever the document is
 * doing, so the "nothing to say yet" cases are explicit: a document with no
 * mockup state has not been seeded, and saying "No template" about it would be
 * news of a transient.
 */
export function mockupStatusKind(doc: DocEntry | undefined, templateStatus: Record<string, TemplateStatus>): MockupStatusKind {
  const mockup = doc?.mockup;
  if (!doc || !mockup) return 'none';
  const readiness = mockupReadiness(doc, templateStatus[mockup.job.templateId]);
  if (readiness === 'no-run') return 'no-run';
  // A template that failed to prepare reads `no-template`, but the document
  // carries the reason (`failTemplate` writes it to every document on that
  // blank) — show that rather than the generic line.
  if (readiness === 'no-template') return mockup.error ? 'error' : 'no-template';
  if (readiness === 'preparing') return 'preparing';
  if (mockup.rendering) return 'rendering';
  if (mockup.error) return 'error';
  // A render LANDED, but from an output the pipeline has moved on from — the shop is
  // looking at a picture of an earlier version of its own artwork. The old inspector
  // hint reported this readiness and the pinned footer did not, so the one line a
  // shop checks after every edit said "Rendered" about a mockup that was not of
  // what it had just made. Below `rendering`, because a re-render already in flight
  // is the more useful news.
  if (readiness === 'stale') return 'stale';
  return mockup.renderedGeneration !== undefined ? 'rendered' : 'none';
}
